import React, { useState, useCallback, useMemo } from "react";
import {
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  useColorModeValue,
  Text,
  Input,
  Button,
  Avatar,
  Select,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Box,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Icon,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Card from "components/card/Card.js";
import { AddIcon } from "@chakra-ui/icons";
import { WalletIcon } from "components/icons/Icons";
import { IoSend } from "react-icons/io5";
import { MdShoppingCart, MdQrCode2, MdArrowDownward, MdHistory, MdSearch } from "react-icons/md";

function CryptoBalancesCard({
  startContent,
  endContent,
  name,
  value,
  cryptoBalances,
  selectedCryptoBalance,
  onBalanceChange,
  controls,
  onControlClick,
  networks,
  onCreateWallet,
  isCreatingWallet,
  onReceiveClick,
  onViewTransactions,
  fiatWallets,
  onGetConversionRates,
  onGetNetworkFees,
  onBuyCrypto,
  onSendCrypto,
  onWithdrawCrypto,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = "secondaryGray.600";
  const dropdownBg = useColorModeValue("white", "gray.800");
  const dropdownBorderColor = useColorModeValue("gray.200", "gray.600");
  const dropdownHoverBg = useColorModeValue("gray.100", "gray.700");
  const breakdownBg = useColorModeValue("gray.50", "gray.700");
  const pendingBg = useColorModeValue("blue.50", "gray.700");
  const pendingLabelColor = useColorModeValue("gray.700", "gray.300");
  const pendingValueColor = useColorModeValue("blue.600", "blue.300");
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isSendModalOpen, onOpen: onSendModalOpen, onClose: onSendModalClose } = useDisclosure();
  const { isOpen: isBuyModalOpen, onOpen: onBuyModalOpen, onClose: onBuyModalClose } = useDisclosure();
  const { isOpen: isWithdrawModalOpen, onOpen: onWithdrawModalOpen, onClose: onWithdrawModalClose } = useDisclosure();

  const [error, setError] = useState([]);
  const [sendError, setSendError] = useState([]);
  const [buyError, setBuyError] = useState([]);
  const [withdrawError, setWithdrawError] = useState([]);
  const [sendData, setSendData] = useState({});
  const [buyData, setBuyData] = useState({});
  const [withdrawData, setWithdrawData] = useState({});
  const [loading, setLoading] = useState(false);
  const [postData, setPostData] = useState({});
  const [addressSearch, setAddressSearch] = useState("");
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);
  const [sendFromAddressSearch, setSendFromAddressSearch] = useState("");
  const [showSendFromAddressDropdown, setShowSendFromAddressDropdown] = useState(false);
  const [sendToAddressSearch, setSendToAddressSearch] = useState("");
  const [showSendToAddressDropdown, setShowSendToAddressDropdown] = useState(false);
  const [withdrawAddressSearch, setWithdrawAddressSearch] = useState("");
  const [showWithdrawAddressDropdown, setShowWithdrawAddressDropdown] = useState(false);

  const handleModalClose = useCallback(() => {
    setPostData({});
    setError([]);
    onModalClose();
  }, [onModalClose]);

  const handleSendModalClose = useCallback(() => {
    setSendData({});
    setSendError([]);
    setSendFromAddressSearch("");
    setShowSendFromAddressDropdown(false);
    setSendToAddressSearch("");
    setShowSendToAddressDropdown(false);
    onSendModalClose();
  }, [onSendModalClose]);

  const handleBuyModalClose = useCallback(() => {
    setBuyData({});
    setBuyError([]);
    setAddressSearch("");
    setShowAddressDropdown(false);
    onBuyModalClose();
  }, [onBuyModalClose]);

  const handleWithdrawModalClose = useCallback(() => {
    setWithdrawData({});
    setWithdrawError([]);
    setWithdrawAddressSearch("");
    setShowWithdrawAddressDropdown(false);
    onWithdrawModalClose();
  }, [onWithdrawModalClose]);

  const getExchangeRates = useCallback(() => {
    if (!buyData?.amount || !buyData?.network || !buyData?.debit_currency || !buyData?.from_address || !buyData?.to_address) {
      setBuyError([{ msg: t("Please fill in all required fields") }]);
      return;
    }
    setLoading(true);
    setBuyError([]);
    onGetConversionRates?.(buyData.amount, buyData.network, buyData.debit_currency, null, (conversionData, error) => {
      if (error || !conversionData) {
        setLoading(false);
        if (error) {
          setBuyError([{ msg: error.message || t("Failed to get conversion rates") }]);
        }
        return;
      }
      setBuyData((prev) => ({ ...prev, selling_price: conversionData, conversion_id: conversionData.conversion_id }));
      onGetNetworkFees?.(buyData.amount, buyData.network, buyData.debit_currency, buyData.from_address, buyData.to_address, null, (feeData, error) => {
        setLoading(false);
        if (error || !feeData) {
          if (error) {
            setBuyError([{ msg: error.message || t("Failed to get network fees") }]);
          }
          return;
        }
        setBuyData((prev) => ({ ...prev, network_fee: feeData, fee_id: feeData.fee_id }));
      });
    });
  }, [buyData, onGetConversionRates, onGetNetworkFees, t]);

  const buyCrypto = useCallback(() => {
    if (!buyData?.amount || !buyData?.network || !buyData?.debit_currency || !buyData?.to_address) {
      setBuyError([{ msg: t("Please fill in all required fields") }]);
      return;
    }
    if (!buyData?.conversion_id || !buyData?.fee_id) {
      setBuyError([{ msg: t("Please verify the transaction first") }]);
      return;
    }
    setLoading(true);
    setBuyError([]);
    onBuyCrypto({
      amount: buyData.amount,
      network: buyData.network,
      debit_currency: buyData.debit_currency,
      address: buyData.to_address,
      conversion_id: buyData.conversion_id,
      fee_id: buyData.fee_id,
      transfer_network: buyData.transfer_network,
    }, (error, data) => {
      setLoading(false);
      if (error) {
        setBuyError([{ msg: error.message || t("Failed to buy crypto") }]);
      } else {
        toast({
          title: t("Success"),
          description: t("Crypto purchase completed successfully"),
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        handleBuyModalClose();
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    });
  }, [buyData, onBuyCrypto, t, handleBuyModalClose, toast]);

  const sendCrypto = useCallback(() => {
    console.log(sendData)
    // TODO: Implement send crypto functionality
  }, [sendData]);

  const withdrawCrypto = useCallback(() => {
    console.log(withdrawData)
    // TODO: Implement withdraw crypto functionality
  }, [withdrawData]);

  const controlButtons = useMemo(() => [
    { key: "create_crypto_wallet", label: t("Create Wallet"), icon: <WalletIcon /> },
    { key: "buy_crypto", label: t("Buy"), icon: <MdShoppingCart /> },
    // { key: "send_crypto", label: t("Send"), icon: <IoSend /> },
    { key: "receive_crypto", label: t("Receive"), icon: <MdQrCode2 /> },
    // { key: "withdraw_crypto", label: t("Withdraw"), icon: <MdArrowDownward /> },
    { key: "view_transactions", label: t("Transactions"), icon: <MdHistory /> },
  ].filter((btn) => btn.key !== "create_crypto_wallet" || networks?.some((item) => !cryptoBalances?.some((balance) => balance?.network === item.currency_short))), [t, networks, cryptoBalances]);

  const handleCreateWallet = useCallback(() => {
    if (!postData?.network) {
      setError([{ msg: t("Please select a network") }]);
      return;
    }
    setError([]);
    onCreateWallet?.(postData, () => {
      setPostData({});
      handleModalClose();
    });
  }, [postData, onCreateWallet, handleModalClose, t]);

  const handleControlClick = useCallback((key) => {
    if (key === "create_crypto_wallet") onModalOpen();
    else if (key === "receive_crypto") onReceiveClick?.();
    else if (key === "send_crypto") onSendModalOpen();
    else if (key === "buy_crypto") onBuyModalOpen();
    else if (key === "withdraw_crypto") onWithdrawModalOpen();
    else if (key === "view_transactions") {
      if (selectedCryptoBalance?.address && selectedCryptoBalance?.network) {
        onViewTransactions?.(selectedCryptoBalance.network, selectedCryptoBalance.address);
      }
    }
    else onControlClick?.(key);
  }, [onModalOpen, onReceiveClick, onSendModalOpen, onBuyModalOpen, onWithdrawModalOpen, onViewTransactions, selectedCryptoBalance, onControlClick]);

  const handleBalanceChange = useCallback((e) => {
    const balance = cryptoBalances.find((item) => item?.network === e.target.value);
    onBalanceChange?.(balance);
  }, [cryptoBalances, onBalanceChange]);

  return (
    <Card py="15px">
      <Flex my="auto" h="100%" align={{ base: "center", xl: "start" }} justify={{ base: "center", xl: "center" }}>
        {startContent}
        <Stat my="auto" ms={startContent ? "18px" : "0px"}>
          <StatLabel lineHeight="100%" color={textColorSecondary} fontSize="sm">
            {name}
          </StatLabel>
          <StatNumber color={textColor} fontSize="2xl">
            {value}
          </StatNumber>
          <Box
            px="12px"
            py="6px"
            borderRadius="12px"
            bg={pendingBg}
            mt="8px"
            width="fit-content"
          >
            <Flex align="center" gap="6px">
              <Text
                fontSize="xs"
                fontWeight="500"
                color={pendingLabelColor}
              >
                {t("Pending")}:
              </Text>
              <Text
                fontSize="sm"
                fontWeight="600"
                color={pendingValueColor}
              >
                {selectedCryptoBalance?.symbol || ""} {Number(selectedCryptoBalance?.pending_received_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 6, maximumFractionDigits: 6 })}
              </Text>
            </Flex>
          </Box>
        </Stat>

        {endContent ? (
          <Flex ms="auto" w="max-content">
            {endContent}
          </Flex>
        ) : (
          <Flex me="-16px" mt="10px" ms="auto">
            <FormLabel htmlFor="crypto-balance">
              <Avatar src={selectedCryptoBalance?.avatar} name={selectedCryptoBalance?.symbol} />
            </FormLabel>
            <Select
              onChange={handleBalanceChange}
              id="crypto-balance"
              variant="filled"
              mt="5px"
              value={selectedCryptoBalance?.network}
              me="20px"
            >
              {cryptoBalances.map((item, index) => (
                <option key={index} value={item?.network}>
                  {item?.network}
                </option>
              ))}
            </Select>
          </Flex>
        )}
      </Flex>

      {controls && (
        <Flex gap="10px" mt="10px" wrap="wrap">
          {controlButtons.map(({ key, label, icon }) => (
            <Button
              key={key}
              onClick={() => handleControlClick(key)}
              colorScheme="brand"
              size="sm"
              leftIcon={icon}
              alignSelf="flex-start"
            >
              {label}
            </Button>
          ))}
        </Flex>
      )}

      <Modal isOpen={isModalOpen} onClose={handleModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Create Wallet")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <select
              required
              onChange={(e) => {
                const transfer_networks = networks?.find((item) => item.currency_short === e.target.value)?.transfer_network || null;
                setPostData((prev) => ({ ...prev, type: "main_wallet", network: e.target.value, transfer_networks: transfer_networks }))
              }}
              style={{
                width: "100%",
                padding: "10px",
                marginBottom: "24px",
                borderRadius: "15px",
                borderColor: "#E2E8F0",
                borderWidth: "1px",
                fontSize: "sm",
                fontWeight: "500",
              }}
            >
              <option value="" disabled selected>{t("Select Crypto Network")}</option>
              {networks?.filter((item) => !cryptoBalances?.some((balance) => balance?.network === item.currency_short)).map((item, i) => (
                <option key={i} value={item.currency_short}>
                  {item.currency_short} - {item.currency_symbol}
                </option>
              ))}
            </select>

            {
              postData?.network && postData?.transfer_networks?.length > 0 && (
                <select
                  required
                  onChange={(e) => {
                    setPostData((prev) => ({ ...prev, transfer_network: e.target.value }))
                  }}
                  style={{
                    width: "100%",
                    padding: "10px",
                    marginBottom: "24px",
                    borderRadius: "15px",
                    borderColor: "#E2E8F0",
                    borderWidth: "1px",
                    fontSize: "sm",
                    fontWeight: "500",
                  }}
                >
                  <option value="" disabled selected>{t("Select Transfer Network")}</option>
                  {postData?.transfer_networks?.map((item, i) => (
                    <option key={i} value={item.network}>
                      {item.name}
                    </option>
                  ))}
                </select>
              )
            }

            {error.length > 0 && error.map((err, index) => (
              <Text key={index} mt="10px" ms="4px" color="red" fontWeight="400" fontSize="sm">
                * {err.msg}
              </Text>
            ))}
          </ModalBody>

          <ModalFooter gap="12px">
            <Button variant="ghost" fontWeight="500" onClick={handleModalClose}>
              {t("Close")}
            </Button>
            <Button
              isLoading={isCreatingWallet || loading}
              isDisabled={isCreatingWallet || loading}
              variant="brand"
              fontWeight="500"
              onClick={handleCreateWallet}
            >
              {t("Proceed")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Send Crypto Modal */}
      <Modal isOpen={isSendModalOpen} onClose={handleSendModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t(`Send ${selectedCryptoBalance?.network} (${selectedCryptoBalance?.symbol || ""})`)}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <select
              required
              onChange={(e) => {
                const transfer_networks = networks?.find((item) => item.currency_short === e.target.value)?.transfer_network || null;
                setSendData((prev) => ({ ...prev, network: e.target.value, transfer_networks: transfer_networks, from_address: "", to_address: "", conversion_id: null, fee_id: null, selling_price: null, network_fee: null }));
                setSendFromAddressSearch("");
                setShowSendFromAddressDropdown(false);
                setSendToAddressSearch("");
                setShowSendToAddressDropdown(false);
              }}
              style={{
                width: "100%",
                padding: "10px",
                marginBottom: "24px",
                borderRadius: "15px",
                borderColor: "#E2E8F0",
                borderWidth: "1px",
                fontSize: "sm",
                fontWeight: "500",
              }}
            >
              <option value="" disabled selected>{t("Select Crypto Network")}</option>
              {networks?.filter((item) => cryptoBalances?.some((balance) => balance?.network === item.currency_short)).map((item, i) => (
                <option key={i} value={item.currency_short}>
                  {item.currency_short} - {item.currency_symbol}
                </option>
              ))}
            </select>

            {sendData?.network && (
              <>
                {/* From Address Selection */}
                <Text mb="8px" fontWeight="500" fontSize="sm">
                  {t("From Address")}
                </Text>
                <Box position="relative" mb="24px">
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" h="full">
                      <Icon as={MdSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      variant="auth"
                      fontSize="sm"
                      value={sendFromAddressSearch || sendData?.from_address || ""}
                      type="text"
                      onChange={(e) => {
                        setSendFromAddressSearch(e.target.value);
                        setSendData((prev) => ({ ...prev, from_address: "", conversion_id: null, fee_id: null, selling_price: null, network_fee: null }));
                        setShowSendFromAddressDropdown(true);
                      }}
                      onFocus={() => setShowSendFromAddressDropdown(true)}
                      placeholder={t("Search or select from address...")}
                      fontWeight="500"
                      size="lg"
                      pl="40px"
                      borderRadius="15px"
                    />
                  </InputGroup>
                  {showSendFromAddressDropdown && (
                    <List
                      position="absolute"
                      top="100%"
                      left="0"
                      right="0"
                      bg={dropdownBg}
                      borderRadius="15px"
                      boxShadow="lg"
                      border="1px solid"
                      borderColor={dropdownBorderColor}
                      maxH="200px"
                      overflowY="auto"
                      zIndex="10"
                      mt="4px"
                    >
                      {cryptoBalances
                        ?.filter((balance) => balance?.network === sendData?.network)
                        ?.filter((balance) => !sendFromAddressSearch || balance?.address?.toLowerCase().includes(sendFromAddressSearch.toLowerCase()))
                        ?.map((balance, i) => (
                          <ListItem
                            key={i}
                            px="16px"
                            py="12px"
                            cursor="pointer"
                            _hover={{ bg: dropdownHoverBg }}
                            onClick={() => {
                              setSendData((prev) => ({ ...prev, from_address: balance.address, conversion_id: null, fee_id: null, selling_price: null, network_fee: null }));
                              setSendFromAddressSearch("");
                              setShowSendFromAddressDropdown(false);
                            }}
                            fontSize="sm"
                            fontWeight="500"
                            borderBottom="1px solid"
                            borderColor={dropdownBorderColor}
                            _last={{ borderBottom: "none" }}
                          >
                            <Text isTruncated>{balance.address}</Text>
                            {balance.label && (
                              <Text fontSize="xs" color="gray.500">{balance.label}</Text>
                            )}
                          </ListItem>
                        ))}
                      {cryptoBalances
                        ?.filter((balance) => balance?.network === sendData?.network)
                        ?.filter((balance) => !sendFromAddressSearch || balance?.address?.toLowerCase().includes(sendFromAddressSearch.toLowerCase()))
                        ?.length === 0 && (
                          <ListItem px="16px" py="12px" fontSize="sm" color="gray.500">
                            {t("No addresses found")}
                          </ListItem>
                        )}
                    </List>
                  )}
                </Box>

                {/* To Address Input */}
                <Text mb="8px" fontWeight="500" fontSize="sm">
                  {t("To Address")}
                </Text>
                <Input
                  variant="auth"
                  fontSize="sm"
                  value={sendData?.to_address || ""}
                  type="text"
                  onChange={(e) => setSendData((prev) => ({ ...prev, to_address: e.target.value, conversion_id: null, fee_id: null, selling_price: null, network_fee: null }))}
                  placeholder={t("Enter recipient address...")}
                  fontWeight="500"
                  size="lg"
                  mb="16px"
                  borderRadius="15px"
                />

                {/* Transfer Network Selection */}
                {sendData?.transfer_networks?.length > 0 && (
                  <select
                    required
                    onChange={(e) => {
                      const selectedNetwork = sendData.transfer_networks.find(n => n.network === e.target.value);
                      setSendData((prev) => ({ ...prev, transfer_network: e.target.value, network_destination_tag: selectedNetwork?.destination_tag, conversion_id: null, fee_id: null, selling_price: null, network_fee: null }));
                    }}
                    style={{
                      width: "100%",
                      padding: "10px",
                      marginBottom: "24px",
                      borderRadius: "15px",
                      borderColor: "#E2E8F0",
                      borderWidth: "1px",
                      fontSize: "sm",
                      fontWeight: "500",
                    }}
                  >
                    <option value="" disabled selected>{t("Select Transfer Network")}</option>
                    {sendData.transfer_networks.map((item, i) => (
                      <option key={i} value={item.network}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                )}

                {/* Destination Tag Field (if required) */}
                {sendData?.network_destination_tag && (
                  <>
                    <Text mb="8px" fontWeight="500" fontSize="sm">
                      {t("Destination Tag")}
                    </Text>
                    <Input
                      variant="auth"
                      fontSize="sm"
                      value={sendData?.destination_tag || ""}
                      type="text"
                      onChange={(e) => setSendData((prev) => ({ ...prev, destination_tag: e.target.value }))}
                      placeholder={t("Enter destination tag...")}
                      fontWeight="500"
                      size="lg"
                      mb="16px"
                      borderRadius="15px"
                    />
                  </>
                )}

                {/* Amount Input */}
                <Input
                  isRequired
                  variant="auth"
                  fontSize="sm"
                  value={sendData?.amount || ""}
                  type="number"
                  onChange={(e) => setSendData((prev) => ({ ...prev, amount: e?.target?.value, conversion_id: null, fee_id: null, selling_price: null, network_fee: null }))}
                  placeholder={t(`Enter amount in ${sendData?.network || ""}`)}
                  mb="10px"
                  fontWeight="500"
                  size="lg"
                />
              </>
            )}

            {sendError.length > 0 && sendError.map((err, index) => (
              <Text key={index} mt="10px" ms="4px" color="red" fontWeight="400" fontSize="sm">
                * {err.msg}
              </Text>
            ))}
          </ModalBody>

          <ModalFooter gap="12px">
            <Button variant="ghost" fontWeight="500" onClick={handleSendModalClose}>
              {t("Close")}
            </Button>
            <Button
              variant="brand"
              fontWeight="500"
              onClick={() => {
                if (!sendData?.from_address || !sendData?.to_address || !sendData?.amount) {
                  setSendError([{ msg: t("Please fill in all required fields") }]);
                  return;
                }
                setSendError([]);
                sendCrypto();
              }}
            >
              {t("Continue")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Buy Crypto Modal */}
      <Modal isOpen={isBuyModalOpen} onClose={handleBuyModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t(`Buy ${selectedCryptoBalance?.network} (${selectedCryptoBalance?.symbol || ""})`)}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <select
              required
              onChange={(e) => {
                const network = networks?.find((item) => item.currency_short === e.target.value);
                setBuyData((prev) => ({ ...prev, network: e.target.value, from_address: network?.[network?.transfer_network?.find(network => network.is_default)?.network || null]?.address || network?.address, to_address: "", conversion_id: null, fee_id: null, selling_price: null, network_fee: null }));
                setAddressSearch("");
                setShowAddressDropdown(false);
              }}
              style={{
                width: "100%",
                padding: "10px",
                marginBottom: "24px",
                borderRadius: "15px",
                borderColor: "#E2E8F0",
                borderWidth: "1px",
                fontSize: "sm",
                fontWeight: "500",
              }}
            >
              <option value="" disabled selected>{t("Select Crypto Network")}</option>
              {networks?.filter((item) => cryptoBalances?.some((balance) => balance?.network === item.currency_short)).map((item, i) => (
                <option key={i} value={item.currency_short}>
                  {item.currency_short} - {item.currency_symbol}
                </option>
              ))}
            </select>

            {buyData?.network && (
              <Box position="relative" mb="24px">
                <InputGroup>
                  <InputLeftElement pointerEvents="none" h="full">
                    <Icon as={MdSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    variant="auth"
                    fontSize="sm"
                    value={addressSearch || buyData?.to_address || ""}
                    type="text"
                    onChange={(e) => {
                      setAddressSearch(e.target.value);
                      setBuyData((prev) => ({ ...prev, to_address: "" }));
                      setShowAddressDropdown(true);
                    }}
                    onFocus={() => setShowAddressDropdown(true)}
                    placeholder={t("Search or select address...")}
                    fontWeight="500"
                    size="lg"
                    pl="40px"
                    borderRadius="15px"
                  />
                </InputGroup>
                {showAddressDropdown && (
                  <List
                    position="absolute"
                    top="100%"
                    left="0"
                    right="0"
                    bg={dropdownBg}
                    borderRadius="15px"
                    boxShadow="lg"
                    border="1px solid"
                    borderColor={dropdownBorderColor}
                    maxH="200px"
                    overflowY="auto"
                    zIndex="10"
                    mt="4px"
                  >
                    {cryptoBalances
                      ?.filter((balance) => balance?.network === buyData?.network)
                      ?.filter((balance) => !addressSearch || balance?.address?.toLowerCase().includes(addressSearch.toLowerCase()))
                      ?.map((balance, i) => (
                        <ListItem
                          key={i}
                          px="16px"
                          py="12px"
                          cursor="pointer"
                          _hover={{ bg: dropdownHoverBg }}
                          onClick={() => {
                            setBuyData((prev) => ({ ...prev, to_address: balance.address, conversion_id: null, fee_id: null, selling_price: null, network_fee: null }));
                            setAddressSearch("");
                            setShowAddressDropdown(false);
                          }}
                          fontSize="sm"
                          fontWeight="500"
                          borderBottom="1px solid"
                          borderColor={dropdownBorderColor}
                          _last={{ borderBottom: "none" }}
                        >
                          <Text isTruncated>{balance.address}</Text>
                          {balance.label && (
                            <Text fontSize="xs" color="gray.500">{balance.label}</Text>
                          )}
                        </ListItem>
                      ))}
                    {cryptoBalances
                      ?.filter((balance) => balance?.network === buyData?.network)
                      ?.filter((balance) => !addressSearch || balance?.address?.toLowerCase().includes(addressSearch.toLowerCase()))
                      ?.length === 0 && (
                        <ListItem px="16px" py="12px" fontSize="sm" color="gray.500">
                          {t("No addresses found")}
                        </ListItem>
                      )}
                  </List>
                )}
              </Box>
            )}

            {buyData?.network && (
              <>
                <select
                  required
                  onChange={(e) => {
                    const selectedWallet = fiatWallets?.find((wallet) => wallet.currency === e.target.value);
                    setBuyData((prev) => ({ ...prev, debit_currency: e.target.value, fiat_wallet: selectedWallet, conversion_id: null, fee_id: null, selling_price: null, network_fee: null }));
                  }}
                  style={{
                    width: "100%",
                    padding: "10px",
                    marginBottom: "24px",
                    borderRadius: "15px",
                    borderColor: "#E2E8F0",
                    borderWidth: "1px",
                    fontSize: "sm",
                    fontWeight: "500",
                  }}
                >
                  <option value="" disabled selected>{t("Select Debit Wallet")}</option>
                  {fiatWallets?.map((wallet, i) => (
                    <option key={i} value={wallet.currency}>
                      {wallet.country_currency_short} - {wallet.country_currency_symbol}{Number(wallet.available_balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </option>
                  ))}
                </select>

                <Input
                  isRequired
                  variant="auth"
                  fontSize="sm"
                  value={buyData?.amount || ""}
                  type="number"
                  onChange={(e) => setBuyData((prev) => ({ ...prev, amount: e?.target?.value, conversion_id: null, fee_id: null, selling_price: null, network_fee: null }))}
                  placeholder={t(`Enter amount in ${buyData?.network || ""}`)}
                  mb="10px"
                  fontWeight="500"
                  size="lg"
                />
              </>
            )}

            {buyData?.conversion_id && buyData?.fee_id && buyData?.selling_price && buyData?.network_fee && (
              <Box
                p="16px"
                borderRadius="15px"
                bg={breakdownBg}
                mb="16px"
              >
                <Text fontSize="sm" fontWeight="600" mb="12px" color={textColor}>
                  {t("Transaction Breakdown")}
                </Text>
                <Flex justifyContent="space-between" mb="8px">
                  <Text fontSize="sm" color={textColorSecondary}>
                    {t("Amount")}
                  </Text>
                  <Text fontSize="sm" fontWeight="500" color={textColor}>
                    {buyData.selling_price.base_amount} {buyData.selling_price.crypto_currency}
                  </Text>
                </Flex>
                <Flex justifyContent="space-between" mb="8px">
                  <Text fontSize="sm" color={textColorSecondary}>
                    {t("Converted Amount")}
                  </Text>
                  <Text fontSize="sm" fontWeight="500" color={textColor}>
                    {Number(buyData.selling_price.exchange_rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {buyData.selling_price.country_currency}
                  </Text>
                </Flex>
                <Flex justifyContent="space-between" mb="8px">
                  <Text fontSize="sm" color={textColorSecondary}>
                    {t("Network Fee")}
                  </Text>
                  <Text fontSize="sm" fontWeight="500" color={textColor}>
                    {buyData.network_fee.fee} {buyData.network_fee.network} ({Number(buyData.network_fee.conversion.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {buyData.network_fee.conversion.currency})
                  </Text>
                </Flex>
                <Box borderTop="1px solid" borderColor={dropdownBorderColor} pt="8px" mt="8px">
                  <Flex justifyContent="space-between">
                    <Text fontSize="sm" fontWeight="600" color={textColor}>
                      {t("Total Cost")}
                    </Text>
                    <Text fontSize="sm" fontWeight="600" color={textColor}>
                      {(Number(buyData.selling_price.exchange_rate) + Number(buyData.network_fee.conversion.amount || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {buyData.selling_price.country_currency}
                    </Text>
                  </Flex>
                </Box>
              </Box>
            )}

            {buyError.length > 0 && buyError.map((err, index) => (
              <Text key={index} mt="10px" ms="4px" color="red" fontWeight="400" fontSize="sm">
                * {err.msg}
              </Text>
            ))}
          </ModalBody>

          <ModalFooter gap="12px">
            <Button variant="ghost" fontWeight="500" onClick={handleBuyModalClose}>
              {t("Close")}
            </Button>
            {buyData?.network && (
              buyData?.conversion_id && buyData?.fee_id ? (
                <Button
                  variant="brand"
                  fontWeight="500"
                  isLoading={loading}
                  isDisabled={loading}
                  onClick={() => {
                    buyCrypto();
                  }}
                >
                  {t("Buy Now")}
                </Button>
              ) : (
                <Button
                  variant="brand"
                  fontWeight="500"
                  isLoading={loading}
                  isDisabled={loading}
                  onClick={() => {
                    if (!buyData?.amount) {
                      setBuyError([{ msg: t(`Please enter an amount in ${buyData?.network || ""}`) }]);
                      return;
                    }
                    setBuyError([]);
                    getExchangeRates();
                  }}
                >
                  {t("Verify")}
                </Button>
              )
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Withdraw Crypto Modal */}
      <Modal isOpen={isWithdrawModalOpen} onClose={handleWithdrawModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t(`Withdraw ${selectedCryptoBalance?.network} (${selectedCryptoBalance?.symbol || ""})`)}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <select
              required
              onChange={(e) => {
                const transfer_networks = networks?.find((item) => item.currency_short === e.target.value)?.transfer_network || null;
                setWithdrawData((prev) => ({ ...prev, network: e.target.value, transfer_networks: transfer_networks, address: "", conversion_id: null, fee_id: null, selling_price: null, network_fee: null }));
                setWithdrawAddressSearch("");
                setShowWithdrawAddressDropdown(false);
              }}
              style={{
                width: "100%",
                padding: "10px",
                marginBottom: "24px",
                borderRadius: "15px",
                borderColor: "#E2E8F0",
                borderWidth: "1px",
                fontSize: "sm",
                fontWeight: "500",
              }}
            >
              <option value="" disabled selected>{t("Select Crypto Network")}</option>
              {networks?.filter((item) => cryptoBalances?.some((balance) => balance?.network === item.currency_short)).map((item, i) => (
                <option key={i} value={item.currency_short}>
                  {item.currency_short} - {item.currency_symbol}
                </option>
              ))}
            </select>

            {withdrawData?.network && (
              <>
                {/* Address Selection */}
                <Text mb="8px" fontWeight="500" fontSize="sm">
                  {t("Address")}
                </Text>
                <Box position="relative" mb="24px">
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" h="full">
                      <Icon as={MdSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      variant="auth"
                      fontSize="sm"
                      value={withdrawAddressSearch || withdrawData?.address || ""}
                      type="text"
                      onChange={(e) => {
                        setWithdrawAddressSearch(e.target.value);
                        setWithdrawData((prev) => ({ ...prev, address: "", conversion_id: null, fee_id: null, selling_price: null, network_fee: null }));
                        setShowWithdrawAddressDropdown(true);
                      }}
                      onFocus={() => setShowWithdrawAddressDropdown(true)}
                      placeholder={t("Search or select address...")}
                      fontWeight="500"
                      size="lg"
                      pl="40px"
                      borderRadius="15px"
                    />
                  </InputGroup>
                  {showWithdrawAddressDropdown && (
                    <List
                      position="absolute"
                      top="100%"
                      left="0"
                      right="0"
                      bg={dropdownBg}
                      borderRadius="15px"
                      boxShadow="lg"
                      border="1px solid"
                      borderColor={dropdownBorderColor}
                      maxH="200px"
                      overflowY="auto"
                      zIndex="10"
                      mt="4px"
                    >
                      {cryptoBalances
                        ?.filter((balance) => balance?.network === withdrawData?.network)
                        ?.filter((balance) => !withdrawAddressSearch || balance?.address?.toLowerCase().includes(withdrawAddressSearch.toLowerCase()))
                        ?.map((balance, i) => (
                          <ListItem
                            key={i}
                            px="16px"
                            py="12px"
                            cursor="pointer"
                            _hover={{ bg: dropdownHoverBg }}
                            onClick={() => {
                              setWithdrawData((prev) => ({ ...prev, address: balance.address, conversion_id: null, fee_id: null, selling_price: null, network_fee: null }));
                              setWithdrawAddressSearch("");
                              setShowWithdrawAddressDropdown(false);
                            }}
                            fontSize="sm"
                            fontWeight="500"
                            borderBottom="1px solid"
                            borderColor={dropdownBorderColor}
                            _last={{ borderBottom: "none" }}
                          >
                            <Text isTruncated>{balance.address}</Text>
                            {balance.label && (
                              <Text fontSize="xs" color="gray.500">{balance.label}</Text>
                            )}
                          </ListItem>
                        ))}
                      {cryptoBalances
                        ?.filter((balance) => balance?.network === withdrawData?.network)
                        ?.filter((balance) => !withdrawAddressSearch || balance?.address?.toLowerCase().includes(withdrawAddressSearch.toLowerCase()))
                        ?.length === 0 && (
                          <ListItem px="16px" py="12px" fontSize="sm" color="gray.500">
                            {t("No addresses found")}
                          </ListItem>
                        )}
                    </List>
                  )}
                </Box>

                {/* Transfer Network Selection */}
                {withdrawData?.transfer_networks?.length > 0 && (
                  <select
                    required
                    onChange={(e) => {
                      const selectedNetwork = withdrawData.transfer_networks.find(n => n.network === e.target.value);
                      setWithdrawData((prev) => ({ ...prev, transfer_network: e.target.value, network_destination_tag: selectedNetwork?.destination_tag, conversion_id: null, fee_id: null, selling_price: null, network_fee: null }));
                    }}
                    style={{
                      width: "100%",
                      padding: "10px",
                      marginBottom: "24px",
                      borderRadius: "15px",
                      borderColor: "#E2E8F0",
                      borderWidth: "1px",
                      fontSize: "sm",
                      fontWeight: "500",
                    }}
                  >
                    <option value="" disabled selected>{t("Select Transfer Network")}</option>
                    {withdrawData.transfer_networks.map((item, i) => (
                      <option key={i} value={item.network}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                )}

                {/* Destination Tag Field (if required) */}
                {withdrawData?.network_destination_tag && (
                  <>
                    <Text mb="8px" fontWeight="500" fontSize="sm">
                      {t("Destination Tag")}
                    </Text>
                    <Input
                      variant="auth"
                      fontSize="sm"
                      value={withdrawData?.destination_tag || ""}
                      type="text"
                      onChange={(e) => setWithdrawData((prev) => ({ ...prev, destination_tag: e.target.value }))}
                      placeholder={t("Enter destination tag...")}
                      fontWeight="500"
                      size="lg"
                      mb="16px"
                      borderRadius="15px"
                    />
                  </>
                )}

                {/* Amount Input */}
                <Input
                  isRequired
                  variant="auth"
                  fontSize="sm"
                  value={withdrawData?.amount || ""}
                  type="number"
                  onChange={(e) => setWithdrawData((prev) => ({ ...prev, amount: e?.target?.value, conversion_id: null, fee_id: null, selling_price: null, network_fee: null }))}
                  placeholder={t(`Enter amount in ${withdrawData?.network || ""}`)}
                  mb="10px"
                  fontWeight="500"
                  size="lg"
                />
              </>
            )}

            {withdrawError.length > 0 && withdrawError.map((err, index) => (
              <Text key={index} mt="10px" ms="4px" color="red" fontWeight="400" fontSize="sm">
                * {err.msg}
              </Text>
            ))}
          </ModalBody>

          <ModalFooter gap="12px">
            <Button variant="ghost" fontWeight="500" onClick={handleWithdrawModalClose}>
              {t("Close")}
            </Button>
            <Button
              variant="brand"
              fontWeight="500"
              onClick={() => {
                if (!withdrawData?.address || !withdrawData?.amount) {
                  setWithdrawError([{ msg: t("Please fill in all required fields") }]);
                  return;
                }
                setWithdrawError([]);
                withdrawCrypto();
              }}
            >
              {t("Continue")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
}

export default React.memo(CryptoBalancesCard);